<template>
  <div class="markdown-editor" :class="{ 'is-disabled': disabled }">
    <div ref="editorRef"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import { marked } from 'marked'
import TurndownService from 'turndown'
import { useDebounceFn } from '@vueuse/core'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])

const editorRef = ref(null)
let quill = null
let isSettingContent = false

const turndown = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  emDelimiter: '*',
  strongDelimiter: '**'
})

function htmlToMarkdown(html) {
  if (!html || html === '<p><br></p>') return ''
  // Quill's getSemanticHTML() emits &nbsp; for EVERY space, and Turndown only
  // collapses ASCII whitespace, so the non-breaking spaces survive into the
  // markdown we store. Left alone, a paragraph pasted into YouTube or Spotify is
  // one unbreakable token that the platform then hyphenates mid-word.
  return turndown.turndown(html).replace(/\u00A0/g, ' ')
}

function markdownToHtml(md) {
  if (!md) return ''
  return marked(md)
}

const emitUpdate = useDebounceFn(() => {
  if (!quill || isSettingContent) return
  const html = quill.getSemanticHTML()
  const md = htmlToMarkdown(html)
  if (md !== props.modelValue) {
    emit('update:modelValue', md)
  }
}, 300)

function setContent(md) {
  if (!quill) return
  isSettingContent = true
  const html = markdownToHtml(md)
  quill.clipboard.dangerouslyPasteHTML(html)
  isSettingContent = false
}

watch(() => props.modelValue, (newVal) => {
  if (isSettingContent || !quill) return
  // Only update if the markdown actually differs from what the editor currently has
  const currentHtml = quill.getSemanticHTML()
  const currentMd = htmlToMarkdown(currentHtml)
  if (currentMd !== newVal) {
    setContent(newVal)
  }
})

watch(() => props.disabled, (val) => {
  if (quill) quill.enable(!val)
})

onMounted(() => {
  quill = new Quill(editorRef.value, {
    theme: 'snow',
    placeholder: props.placeholder,
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link'],
        ['clean']
      ]
    }
  })

  if (props.disabled) quill.enable(false)

  // Set initial content
  if (props.modelValue) {
    setContent(props.modelValue)
  }

  quill.on('text-change', () => {
    if (!isSettingContent) emitUpdate()
  })

  // Quill's capture-phase copy handler sets text/html from getSemanticHTML(),
  // which turns every space into &nbsp;. Rich-text targets (Podbean, Spotify,
  // Apple) then paste one unbreakable run per paragraph and hyphenate mid-word.
  // Runs after Quill's handler (bubble phase) and rewrites the HTML payload.
  quill.root.addEventListener('copy', fixClipboardHtml)
  quill.root.addEventListener('cut', fixClipboardHtml)
})

function fixClipboardHtml(e) {
  const html = e.clipboardData?.getData('text/html')
  if (html) e.clipboardData.setData('text/html', html.replace(/&nbsp;|\u00A0/g, ' '))
}

onBeforeUnmount(() => {
  if (quill) {
    quill.off('text-change')
    quill = null
  }
})
</script>

<style lang="scss" scoped>
.markdown-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  // clip, not hidden: hidden makes this a scroll container, which would anchor
  // the sticky toolbar below to a box that never scrolls
  overflow: clip;

  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  :deep(.ql-toolbar) {
    border: 1px solid var(--bulma-border, hsl(0, 0%, 86%));
    border-radius: 4px 4px 0 0;
    background: var(--bulma-scheme-main-bis, #fafafa);
    flex-shrink: 0;
    // Follow the reader down long documents. No-op where the editor has a fixed
    // height and .ql-container does the scrolling (admin generator panel).
    position: sticky;
    top: 0;
    z-index: 5;
  }

  :deep(.ql-container) {
    border: 1px solid var(--bulma-border, hsl(0, 0%, 86%));
    border-top: none;
    border-radius: 0 0 4px 4px;
    font-size: 0.85rem;
    line-height: 1.6;
    flex: 1;
    overflow-y: auto;
  }

  :deep(.ql-editor) {
    min-height: 200px;
    padding: 1.5rem 2rem;

    // Match Bulma .content spacing for consistent preview/edit feel
    h1 {
      font-size: 2em;
      margin-bottom: 0.5em;
      margin-top: 1em;
      font-weight: 700;
      line-height: 1.125;
    }

    h2 {
      font-size: 1.75em;
      margin-bottom: 0.5em;
      margin-top: 1em;
      font-weight: 700;
      line-height: 1.125;
    }

    h3 {
      font-size: 1.25em;
      margin-bottom: 0.5em;
      margin-top: 1em;
      font-weight: 700;
      line-height: 1.25;
    }

    p {
      margin-bottom: 1em;
    }

    ul, ol {
      margin-bottom: 1em;
      margin-left: 2em;

      li + li {
        margin-top: 0.25em;
      }
    }

    blockquote {
      padding: 0.75em 1.25em;
      border-left: 4px solid var(--bulma-border, hsl(0, 0%, 86%));
      background: var(--bulma-scheme-main-bis, #fafafa);
      margin-bottom: 1em;
    }
  }

  :deep(.ql-editor.ql-blank::before) {
    color: var(--bulma-text-weak, hsl(0, 0%, 60%));
    font-style: normal;
  }
}
</style>
